import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import TestingProjectName from '@/pages/OptimisationProject/components/TestingProjectName';
import { ChannelResource } from '@/types/resources';
import { api } from '@/utils/api';
import { TestingCurrencies } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import { hexToRgba } from '@/utils/theme';

import AspectRatioNote from './AspectRatioNote';

const AddButtonHolder = styled.div`
  text-align: right;
  margin-top: 24px;
  svg {
    margin-right: 8px;
  }
  button {
    width: 100%;
  }
`;

export const BatchBox = styled.div`
  background: ${({ theme }) => hexToRgba(theme.colors.grayscaleLight, 0.5)};
  padding: 24px 32px 32px;
  margin: 32px -32px 0;
`;

export const BatchBoxHeading = styled(Text)`
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const RemoveButton = styled.button`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
    font-weight: ${theme.typography.button.fontWeight};
    color: ${theme.colors.error};
    background: ${theme.colors.errorLight};
  `}
  border-radius: 4px;
  border: solid 1px transparent;
  padding: 0 11px;
  display: inline-flex;
  height: 27px;
  align-items: center;
  font-size: 11px;
  line-height: 15px;
  transition: border-color ease-out 0.2s;
  &:hover {
    border: solid 1px;
  }
`;

const DEFAULT_BID_AMOUNT = 1686;

const EMPTY_VARIATION = { message: '', image: '', name: '', link: '' };

type FormDataT = {
  channel_id: number | null;
  budget: string;
  settings: {
    clicks_threshold: string;
    target_country: string;
    target_device: string;
    bid_amount: string;
  };
  variations: {
    message: string;
    image: string;
    link: string;
    name: string;
  }[];
};

interface Props {
  onClose: () => void;
  projectId: number;
  projectName: string;
}

const TestsModal: FC<Props> = ({ onClose, projectId, projectName }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const { data: channelOptions } = useQuery(
    ['channels', { onlyBp: true, authorized: true }],
    () =>
      api.get<never, ChannelResource[]>(`/v1/channels/authorized`, {
        params: {
          only_bp: 1,
        },
      }),
    {
      staleTime: 1000 * 120,
      select: (data) =>
        data.map((channel) => ({ label: channel.name, value: channel.id })),
    },
  );

  const {
    control,
    handleSubmit,
    clearErrors,
    setError,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      channel_id: null,
      settings: {
        clicks_threshold: '100',
        target_country: '',
        target_device: '',
        bid_amount: DEFAULT_BID_AMOUNT.toString(),
      },
      budget: '',
      variations: [{ ...EMPTY_VARIATION }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'variations',
  });

  const onCancel = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.post('/v1/editorial/bp-tests', {
          ...data,
          bp_testing_project_id: projectId,
        });

        await queryClient.invalidateQueries(['bp_tests', { projectId }]);

        alerts.success(`Tests have been successfully created`);

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading={`Upload tests`} onClose={onCancel}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <TestingProjectName projectTitle={projectName} />
              </Larger>
              <Larger>
                <AspectRatioNote />
              </Larger>
              <FormField<FormDataT, number>
                control={control}
                name="channel_id"
                label="Facebook Page"
                type="select"
                options={channelOptions ?? []}
              />
              <FormField<FormDataT>
                control={control}
                name="budget"
                label="Budget"
                subLabel="(per variation)"
                type="currency"
                currency={TestingCurrencies.IDR}
              />
              <FormField<FormDataT>
                control={control}
                name="settings.bid_amount"
                placeholder="Add bid amount"
                label="Bid amount"
                type="currency"
                currency={TestingCurrencies.IDR}
              />
              <FormField<FormDataT>
                control={control}
                name="settings.clicks_threshold"
                label="Clicks Threshold"
                placeholder="Add clicks threshold"
              />
              <FormField<FormDataT, string>
                control={control}
                name="settings.target_country"
                label="Target Country"
                type="select"
                options={[
                  {
                    label: 'Worldwide',
                    value: '',
                  },
                  {
                    label: 'US',
                    value: 'US',
                  },
                ]}
              />
              <FormField<FormDataT, string>
                control={control}
                name="settings.target_device"
                label="Target Device"
                type="select"
                options={[
                  {
                    label: 'All',
                    value: '',
                  },
                  {
                    label: 'Desktop',
                    value: 'desktop',
                  },
                  {
                    label: 'Mobile',
                    value: 'mobile',
                  },
                ]}
              />
            </FormLayout>
            {fields.map((item, index) => (
              <BatchBox key={item.id}>
                <BatchBoxHeading $type="button">
                  Test variation #{index + 1}
                  {index > 0 && (
                    <RemoveButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      Remove
                    </RemoveButton>
                  )}
                </BatchBoxHeading>
                <FormLayout>
                  <FormField<FormDataT>
                    control={control}
                    name={`variations.${index}.name`}
                    label="Name"
                    placeholder="Add name"
                  />
                  <FormField<FormDataT>
                    control={control}
                    name={`variations.${index}.link`}
                    label="Link"
                    inputType="url"
                    placeholder="Add link"
                  />
                  <FormField<FormDataT>
                    control={control}
                    name={`variations.${index}.message`}
                    label="Message"
                    placeholder="Add message"
                  />
                  <FormField<FormDataT>
                    control={control}
                    name={`variations.${index}.image`}
                    type="image"
                    id={`variations-${item.id}-image`}
                    notes="JPG, PNG. Aspect ratio: 1x1"
                  />
                </FormLayout>
              </BatchBox>
            ))}
            <AddButtonHolder>
              <Button
                type="button"
                $type="colorized"
                onClick={() => append({ ...EMPTY_VARIATION })}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 2V14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 8H14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Add variation
              </Button>
            </AddButtonHolder>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Upload
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default TestsModal;
